import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
// Api
import client from '../../api/client';
// Constants
import { HOME_PAGE_URL } from '../../utils/Routes';
import { RESET_PASSWORD_API } from '../../utils/ApiRoutes';
// Icons
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
// Hooks
import useNavigateToPage from '../../hooks/useNavigateToPage';
// Components
import AppLink from '../utils/AppLink';
import LoadingSpinner from '../utils/LoadingSpinner';

// Password policy:
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s])(?!.*[\\/'"]).{8,}$/;

function ResetLostPasswordForm() {
  const { userId, token } = useParams();
  const navigateToPage = useNavigateToPage();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    userId: userId,
    token: token,
  });
  const [inProgress, setInProgress] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // optional: redirect to login after success
  const [redirectIn, setRedirectIn] = useState(5);
  const timerRef = useRef(null);

  const passwordRef = useRef(null);
  const confirmRef = useRef(null);
  const alertRef = useRef(null);

  useEffect(() => {
    if (errorMsg) {
      // focus first invalid field based on message heuristics; else live region
      const msg = String(errorMsg).toLowerCase();
      if (
        (msg.includes('match') || msg.includes('confirm')) &&
        confirmRef.current
      ) {
        confirmRef.current.focus();
      } else if (passwordRef.current) {
        passwordRef.current.focus();
      } else if (alertRef.current) {
        alertRef.current.focus();
      }
    }
  }, [errorMsg]);

  useEffect(() => {
    if (successMsg) {
      // countdown to login page
      timerRef.current = setInterval(() => {
        setRedirectIn((s) => {
          if (s <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [successMsg, navigateToPage]);

  const onChange = (e) => {
    setErrorMsg(null);
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validate = () => {
    const { password, confirmPassword } = formData;
    if (!PASSWORD_REGEX.test(password)) {
      return 'Password must be at least 8 characters, include 1 uppercase letter, 1 number, and 1 special character, and must not contain /, \\, ", or \'.';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match. Please re-enter them.';
    }
    return null;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const fieldError = validate();
    if (fieldError) {
      setErrorMsg(fieldError);
      return;
    }

    if (!userId || !token) {
      setErrorMsg('Invalid or missing reset link parameters.');
      return;
    }

    setInProgress(true);

    client
      .post(RESET_PASSWORD_API, formData, false)
      .then(() => {
        setInProgress(false);
        setSuccessMsg('Your password has been updated. Redirecting to login…');
      })
      .catch((err) => {
        const apiMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to reset your password. Please try again.';
        setErrorMsg(apiMsg);
        setInProgress(false);
      });
  };

  return (
    <form
      className='grid gap-y-4'
      onSubmit={onSubmit}
      aria-describedby='reset-help'
      aria-busy={inProgress ? 'true' : 'false'}
      noValidate
    >
      <p id='reset-help' className='sr-only'>
        Enter and confirm a new password. Password must be at least 8
        characters, include an uppercase letter, a number, and a special
        character, and must not contain slash, backslash, or quotes.
      </p>

      {/* Password */}
      <div className='grid gap-y-2'>
        <label
          htmlFor='password'
          className='block text-center text-sm font-medium text-colour8 dark:text-colour3'
        >
          New password
        </label>
        <div className='relative'>
          <input
            ref={passwordRef}
            type={showPassword ? 'text' : 'password'}
            id='password'
            name='password'
            className='bg-colour7 border-solid border-colour3 border-2 w-full rounded-md py-2 px-2'
            placeholder='••••••••'
            value={formData.password}
            onChange={onChange}
            required
            autoComplete='new-password'
            aria-invalid={errorMsg ? 'true' : 'false'}
            aria-describedby={errorMsg ? 'password-error' : 'password-hint'}
          />
          <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
            <button
              type='button'
              onClick={() => setShowPassword((s) => !s)}
              className='flex items-center text-colour5 text-2xl'
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
            </button>
          </div>
        </div>
        <span id='password-hint' className='sr-only'>
          At least 8 characters, with an uppercase letter, a number, a special
          character, and no slash, backslash, or quotes.
        </span>
        <span id='password-error' className='sr-only'>
          {errorMsg ? String(errorMsg) : ''}
        </span>
      </div>

      {/* Confirm */}
      <div className='grid gap-y-2'>
        <label
          htmlFor='confirmPassword'
          className='block text-center text-sm font-medium text-colour8 dark:text-colour3'
        >
          Confirm new password
        </label>
        <div className='relative'>
          <input
            ref={confirmRef}
            type={showConfirm ? 'text' : 'password'}
            id='confirmPassword'
            name='confirmPassword'
            className='bg-colour7 border-solid border-colour3 border-2 w-full rounded-md py-2 px-2'
            placeholder='••••••••'
            value={formData.confirmPassword}
            onChange={onChange}
            required
            autoComplete='new-password'
            aria-invalid={errorMsg ? 'true' : 'false'}
            aria-describedby={errorMsg ? 'confirm-error' : 'confirm-hint'}
          />
          <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
            <button
              type='button'
              onClick={() => setShowConfirm((s) => !s)}
              className='flex items-center text-colour5 text-2xl'
              aria-label={
                showConfirm ? 'Hide confirm password' : 'Show confirm password'
              }
            >
              {showConfirm ? <IoEyeOffOutline /> : <IoEyeOutline />}
            </button>
          </div>
        </div>
        <span id='confirm-hint' className='sr-only'>
          Re-enter the same password.
        </span>
        <span id='confirm-error' className='sr-only'>
          {errorMsg ? String(errorMsg) : ''}
        </span>
      </div>

      {/* Submit */}
      <div className='grid h-fit w-full'>
        <button
          type='submit'
          className='px-4 py-2 rounded-lg bg-colour3 font-bold active:scale-90 text-colour1 hover:bg-colour4 active:opacity-80 transition duration-300'
          disabled={inProgress}
          aria-label='Set new password'
        >
          {inProgress ? (
            <div
              className='flex justify-center items-center gap-2'
              aria-live='polite'
            >
              <LoadingSpinner xs />
              <span className='sr-only'>Updating password…</span>
            </div>
          ) : (
            <span>Update password</span>
          )}
        </button>
      </div>

      {/* Live region */}
      {(errorMsg || successMsg) && (
        <div
          ref={alertRef}
          role='alert'
          aria-live={errorMsg ? 'assertive' : 'polite'}
          aria-atomic='true'
          tabIndex={-1}
          className={
            errorMsg
              ? 'text-error-red max-w-[20rem] mx-auto text-sm text-center'
              : 'text-green-600 max-w-[20rem] mx-auto text-sm text-center'
          }
        >
          {String(errorMsg || successMsg)}
          {successMsg && redirectIn > 0
            ? ` Redirecting in ${redirectIn}s…`
            : ''}
        </div>
      )}
    </form>
  );
}

export default ResetLostPasswordForm;
